import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';
import Avatar from '@atlaskit/avatar';
import { IconButton, LinkButton } from '@atlaskit/button/new';
import EmailIcon from '@atlaskit/icon/core/email';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import { useAIAnswerContext } from '../ai-answer-dialog/ai-answer-context';
import { SainAction, useAnalytics, useSAINUsageCriteriaMet } from '../common/controller/analytics';
import { messages } from '../messages';
import { onKnowledgeCardLinkClick } from './analytics';
import { onEmailCopy } from './util';
var wrapperXcss = xcss({
  ':hover': {
    boxShadow: 'elevation.shadow.overlay',
    borderRadius: 'border.radius.200'
  }
});
var cardXcss = xcss({
  backgroundColor: 'elevation.surface.raised',
  borderRadius: 'border.radius.200',
  boxShadow: 'elevation.shadow.raised',
  padding: 'space.200',
  width: '214px'
});
var clickableCardXcss = xcss({
  backgroundColor: 'elevation.surface.raised',
  borderRadius: 'border.radius.200',
  boxShadow: 'elevation.shadow.raised',
  padding: 'space.200',
  width: '214px',
  cursor: 'pointer'
});
var userNameXcss = xcss({
  color: 'color.text',
  font: "var(--ds-font-heading-small, normal 600 16px/20px ui-sans-serif, -apple-system, BlinkMacSystemFont, \"Segoe UI\", Ubuntu, \"Helvetica Neue\", sans-serif)",
  textAlign: 'center',
  wordBreak: 'break-all',
  display: '-webkit-box',
  overflow: 'hidden',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 1
});
var tooltip = xcss({
  color: 'color.text.inverse',
  font: 'font.body.UNSAFE_small'
});
var tooltipContainer = xcss({
  backgroundColor: 'color.background.neutral.bold',
  borderRadius: '3px',
  alignItems: 'center',
  zIndex: 'tooltip',
  position: 'absolute',
  left: 'space.025',
  bottom: 'space.negative.250',
  maxWidth: '240px',
  paddingBlock: 'space.025',
  paddingInline: 'space.075'
});
var userSubheadingXcss = xcss({
  color: 'color.text.subtle',
  font: 'font.body',
  textAlign: 'center',
  wordBreak: 'break-all',
  display: '-webkit-box',
  overflow: 'hidden',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 1
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
var tooltipOptions = {
  position: 'bottom'
};
export var PersonCard = function PersonCard(_ref) {
  var name = _ref.name,
    profilePicUrl = _ref.profilePicUrl,
    jobTitle = _ref.jobTitle,
    profileUrl = _ref.profileUrl,
    email = _ref.email,
    _ref$isHoverState = _ref.isHoverState,
    isHoverState = _ref$isHoverState === void 0 ? true : _ref$isHoverState,
    onCardClick = _ref.onCardClick;
  var _useAIAnswerContext = useAIAnswerContext(),
    commonAttributes = _useAIAnswerContext.commonAttributes;
  var _useAnalytics = useAnalytics(),
    _useAnalytics2 = _slicedToArray(_useAnalytics, 1),
    fireAnalyticsEvent = _useAnalytics2[0];
  var _useSAINUsageCriteria = useSAINUsageCriteriaMet(),
    fireSAINUsageEvent = _useSAINUsageCriteria.fireSAINUsageEvent;
  var _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    tipState = _useState2[0],
    setTipState = _useState2[1];
  var Wrapper = !isHoverState && !!onCardClick ? function (props) {
    return /*#__PURE__*/React.createElement(Box, {
      xcss: wrapperXcss,
      onClick: function onClick() {
        fireSAINUsageEvent({
          metCriteria: SainAction.PERSON_SELECTED
        });
        fireAnalyticsEvent(onKnowledgeCardLinkClick(commonAttributes, 'person-card-click'));
        onCardClick();
      }
    }, props.children);
  } : React.Fragment;
  return /*#__PURE__*/React.createElement(Wrapper, null, /*#__PURE__*/React.createElement(Flex, {
    direction: "column",
    alignItems: "center",
    gap: "space.200",
    xcss: !isHoverState && !!onCardClick ? clickableCardXcss : cardXcss
  }, /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Avatar, {
    src: profilePicUrl,
    size: "xlarge"
  })), /*#__PURE__*/React.createElement(Flex, {
    direction: "column",
    alignItems: "center",
    gap: "space.100"
  }, /*#__PURE__*/React.createElement(Box, {
    xcss: userNameXcss
  }, name), jobTitle && /*#__PURE__*/React.createElement(Box, {
    xcss: userSubheadingXcss
  }, jobTitle)), /*#__PURE__*/React.createElement(Flex, {
    gap: "space.050"
  }, email && /*#__PURE__*/React.createElement(IconButton, {
    icon: function icon(props) {
      return /*#__PURE__*/React.createElement(EmailIcon, _extends({}, props, {
        color: "var(--ds-icon, #44546F)"
      }));
    },
    onClick: function onClick() {
      fireAnalyticsEvent(onKnowledgeCardLinkClick(commonAttributes, 'person-card-copy-email'));
      fireSAINUsageEvent({
        metCriteria: SainAction.COPY_CLICKED
      });
      onEmailCopy(email);
    },
    label: formatMessage(messages.knowledge_cards_copy_email_address),
    onMouseEnter: function onMouseEnter() {
      return setTipState(true);
    },
    onMouseLeave: function onMouseLeave() {
      return setTipState(false);
    },
    isTooltipDisabled: isHoverState,
    tooltip: tooltipOptions
  }), profileUrl && /*#__PURE__*/React.createElement(LinkButton, {
    href: profileUrl,
    target: "_blank",
    onClick: function onClick() {
      fireAnalyticsEvent(onKnowledgeCardLinkClick(commonAttributes, 'person-card-view-profile'));
      fireSAINUsageEvent({
        metCriteria: SainAction.VIEW_PROFILE_CLICKED
      });
    }
  }, /*#__PURE__*/React.createElement(FormattedMessage, messages.ai_answer_who_questions_view_profile)), isHoverState && tipState && /*#__PURE__*/React.createElement(Box, {
    xcss: tooltipContainer
  }, /*#__PURE__*/React.createElement(Box, {
    xcss: tooltip
  }, formatMessage(messages.knowledge_cards_copy_email_address))))));
};